// src/js/core/quizUI.js
import {
  getWordsForPrompt,
  getCurrentLanguage,
  getCurrentLanguageWords,
  updateCurrentLanguageWords,
} from "./wordManager.js";
import { getTranslation } from "./translateWords.js";
import { updateUnknownWordsList } from "./wordListUI.js";

let quizModal = null;

function createQuizModal() {
  const dialog = document.createElement("dialog");
  dialog.id = "quiz-modal";

  dialog.innerHTML = `
      <div id="quiz-content"></div>
      <div id="quizBottomControls">
        <p id="quizResult"></p>
        <button id="quizActionButton" disabled>Reveal it</button>
      </div>
      <button id="close-quiz" class="closeDialogBtn" aria-label="Close quiz">×</button>
  `;

  document.body.appendChild(dialog);

  // Close button handler
  dialog.querySelector("#close-quiz").addEventListener("click", () => {
    dialog.close();
  });

  // Cleanup handler for when dialog closes (regardless of how it was closed)
  dialog.addEventListener("close", () => {
    const quizContent = document.getElementById("quiz-content");
    const quizResult = document.getElementById("quizResult");
    const quizBottomControls = document.getElementById("quizBottomControls");
    const quizActionButton = document.getElementById("quizActionButton");
    const removalPrompt = document.getElementById("quizRemovalPrompt");

    // Reset all content and states
    quizContent.innerHTML = "";
    quizResult.textContent = "";
    quizBottomControls.classList.remove("correct", "incorrect");
    quizResult.classList.remove("correct", "incorrect");
    quizActionButton.style.display = "block";
    quizActionButton.textContent = "Check";
    quizActionButton.disabled = true;

    // Remove the word removal UI if it exists
    if (removalPrompt) {
      removalPrompt.remove();
    }
  });

  // Add handler for quiz options selection
  dialog.addEventListener("change", (e) => {
    if (e.target.matches('input[type="radio"]')) {
      // Enable the check button when an option is selected
      document.getElementById("quizActionButton").disabled = false;
    }
  });

  // Add handler for check/new quiz button
  dialog.querySelector("#quizActionButton").addEventListener("click", async (e) => {
    const button = e.target;
    if (button.textContent === "Check") {
      handleCheck();
    } else if (button.textContent === "New quiz") {
      // Clear existing content immediately
      const quizContent = document.getElementById("quiz-content");
      const quizResult = document.getElementById("quizResult");
      const quizBottomControls = document.getElementById("quizBottomControls");
      const removalPrompt = document.getElementById("quizRemovalPrompt");

      quizContent.innerHTML = '<p style="text-align: center; padding: 2rem;">Summoning your quiz...</p>';
      quizResult.textContent = "";
      quizBottomControls.classList.remove("correct", "incorrect");
      quizResult.classList.remove("correct", "incorrect");

      // Clean up removal prompt if it exists
      if (removalPrompt) {
        removalPrompt.remove();
      }

      // Show loading state
      button.innerHTML = '<div class="spin"></div>';
      button.disabled = true;

      // Get and display new quiz
      await generateNewQuiz();
    }
  });

  return dialog;
}

async function handleCheck() {
  const quizContent = document.getElementById("quiz-content");
  const quizBottomControls = document.getElementById("quizBottomControls");
  const quizResult = document.getElementById("quizResult");
  const quizActionButton = document.getElementById("quizActionButton");

  // Get selected answer and correct answer
  const selectedOption = quizContent.querySelector('input[type="radio"]:checked')?.closest(".quiz-option");
  const correctOption = quizContent.querySelector(".correct.quiz-option");

  if (!selectedOption || !correctOption) return;

  // Get the text content (word) from the options, removing any whitespace
  const selectedWord = selectedOption.textContent.trim();
  const correctWord = correctOption.textContent.trim();

  // Get translation of the correct word
  const language = getCurrentLanguage();
  const translationData = await getTranslation(correctWord, language);
  const translation = translationData?.value || translationData || "translation unavailable";

  const isCorrect = selectedOption.classList.contains("correct");

  // Update UI based on correctness
  quizBottomControls.classList.add(isCorrect ? "correct" : "incorrect");
  quizResult.classList.add(isCorrect ? "correct" : "incorrect");

  if (isCorrect) {
    quizResult.innerHTML = `✅ Nice! "${correctWord}" = "${translation}"`;

    // Add word removal UI
    const wordRemovalDiv = document.createElement("div");
    wordRemovalDiv.id = "quizRemovalPrompt";
    wordRemovalDiv.innerHTML = `
      <p>Ready to retire "${correctWord}"? You've learned it well.</p>
      <div style="display: flex; gap: 0.75rem;">
        <button id="quizRemoveWordBtn">Yep! I’ve got it down.</button>
        <button id="quizKeepWordBtn">Let’s keep practicing it.</button>
      </div>
    `;

    // Hide the regular action button
    quizActionButton.style.display = "none";

    // Add the removal UI
    quizBottomControls.appendChild(wordRemovalDiv);

    // Add click handlers for the removal buttons
    const removeWordBtn = document.getElementById("quizRemoveWordBtn");
    const keepWordBtn = document.getElementById("quizKeepWordBtn");

    removeWordBtn.addEventListener("click", () => {
      // Remove word from saved words
      const currentWords = getCurrentLanguageWords();
      currentWords.delete(correctWord);
      updateCurrentLanguageWords(currentWords);

      // Update the UI
      wordRemovalDiv.innerHTML = `<p>"${correctWord}" was removed from your list. Onward!</p>`;

      // Show and update the action button
      quizActionButton.style.display = "block";
      quizActionButton.textContent = "New quiz";

      // Update the unknown words list in the main UI
      updateUnknownWordsList();
    });

    keepWordBtn.addEventListener("click", () => {
      // Remove the word removal UI
      wordRemovalDiv.remove();

      // Show and update the action button
      quizActionButton.style.display = "block";
      quizActionButton.textContent = "New quiz";
    });
  } else {
    quizResult.innerHTML = `❌ Almost! The right answer was: "${correctWord}" (${translation})`;
    quizActionButton.textContent = "New quiz";
  }
}

async function generateNewQuiz() {
  try {
    const currentWords = getCurrentLanguageWords();
    // Check if we still have enough words
    if (currentWords.size < 4) {
      const quizContent = document.getElementById("quiz-content");
      quizContent.innerHTML = `
        <p style="text-align: center; text-wrap: balance;">⚠️ You'll need at least 4 saved words to start a quiz. Generate some dialogues and tap any word you want to learn.</p>
        <button onclick="this.closest('dialog').close()" 
                class="quiz-close-button">
          Got it
        </button>
      `;
      quizModal.querySelector("#quizActionButton").style.display = "none";
      return;
    }
    // Proceed with quiz generation if we have enough words
    // Get 4 random words and current language
    const words = getWordsForPrompt(4);
    const language = getCurrentLanguage();

    const response = await fetch("/.netlify/functions/quiz", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        words,
        language,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const quizContent = document.getElementById("quiz-content");
    const quizActionButton = document.getElementById("quizActionButton");

    if (data.html) {
      quizContent.innerHTML = data.html;
      quizActionButton.disabled = true;
      quizActionButton.textContent = "Check";
    } else {
      throw new Error("Invalid quiz response format");
    }
  } catch (error) {
    console.error("Error:", error);
    const quizContent = document.getElementById("quiz-content");
    quizContent.innerHTML = "Whoops! Something went wrong fetching your quiz. Let's try again.";
  }
}

async function handleQuizStart() {
  // Get reference to quiz button
  const quizButton = document.getElementById("quiz-button");
  const buttonOriginalContent = quizButton.innerHTML;

  // Show loading state
  quizButton.innerHTML = '<div class="spin"></div>';
  quizButton.disabled = true;

  try {
    // Create modal on first use if it doesn't exist
    if (!quizModal) {
      quizModal = createQuizModal();
    }

    await generateNewQuiz();

    // Show the modal
    quizModal.showModal();
  } catch (error) {
    console.error("Error:", error);
  } finally {
    // Restore button state
    quizButton.innerHTML = buttonOriginalContent;
    quizButton.disabled = false;
  }
}

export function initializeQuiz() {
  // Just add click handler, modal creation deferred until needed
  document.getElementById("quiz-button")?.addEventListener("click", handleQuizStart);
}
